import type { EFormQuestion, EFormSubmitAnswer } from './eform.types'
import { toSubmitAnswers } from './eformAnswers'
import { DISPLAY_ONLY_TYPES, HIDDEN_TYPES } from './question-types'

/**
 * Partial saves (R34, KTD15): with the account's setting on, the form saves each
 * answer as it is entered, so a visitor who leaves an unfinished form still
 * shows in the Incomplete list with the answers given so far.
 *
 * - A choice (radio, checkbox, dropdown, scale, stars, date, opt-in, consent,
 *   file) saves as soon as it changes.
 * - A typed answer saves after a pause in typing, or when its field loses focus
 *   (FTP saved on its inputs' change event, which fires on blur). The signature
 *   pad counts as typed: it reports every stroke. So does a choice whose "Other"
 *   box is in use: its text arrives a keystroke at a time.
 * - Hidden, display-only and payment questions never save before Submit.
 *
 * Every save goes through the visit's write queue (eformQueue), so the first
 * one creates the visit's response and every later one, the submit included,
 * writes to that same response. A failed save is left to Submit, which sends
 * every answer; a form that can no longer save (setting off, already
 * submitted, preview) stops trying.
 */
export interface EFormAutosave {
  /** An answer changed. */
  change(question: EFormQuestion, value: string | string[]): void
  /** The question's field lost focus: its pending typed answer saves now. */
  blur(orderId: number): void
  /** Drops the pending typed answers: Submit has sent every answer. */
  cancelPending(): void
}

/** Saved after a pause in typing or on blur: text boxes, review, text line, BMI, name, address, signature. */
export const TYPED_TYPES = new Set([3, 6, 9, 10, 11, 17, 18, 22, 25])
/** Never saved before Submit: page break, Submit button, payment details. */
const NOT_SAVED_TYPES = new Set([13, 20, 21])
/** The pause in typing after which a typed answer saves. */
export const TYPING_PAUSE_MS = 1500
/** Answers the server will never take for this visit. */
const STOP_CODES = new Set(['PARTIAL_SAVES_OFF', 'ALREADY_SUBMITTED', 'PREVIEW_ONLY', 'INVALID_VISIT'])

/** A choice's "Other" box sends its text as `other:<text>` on every keystroke. */
const hasOtherText = (value: string | string[]) =>
  (Array.isArray(value) ? value : [value]).some((v) => v.startsWith('other:'))

export function createAutosave(
  save: (answer: EFormSubmitAnswer) => Promise<unknown>,
  questions: EFormQuestion[],
  pauseMs: number = TYPING_PAUSE_MS
): EFormAutosave {
  const pending = new Map<number, { value: string | string[]; timer: ReturnType<typeof setTimeout> }>()
  let stopped = false

  const send = (orderId: number, value: string | string[]) => {
    if (stopped) return
    const [answer] = toSubmitAnswers({ [orderId]: value }, questions)
    save(answer).catch((err: { code?: string } | undefined) => {
      if (err?.code && STOP_CODES.has(err.code)) {
        stopped = true
        cancelAll()
      }
    })
  }

  const drop = (orderId: number) => {
    const entry = pending.get(orderId)
    if (entry) {
      clearTimeout(entry.timer)
      pending.delete(orderId)
    }
    return entry
  }

  const cancelAll = () => {
    for (const entry of pending.values()) clearTimeout(entry.timer)
    pending.clear()
  }

  const flush = (orderId: number) => {
    const entry = drop(orderId)
    if (entry) send(orderId, entry.value)
  }

  return {
    change(question, value) {
      const type = question.questionType
      if (stopped || HIDDEN_TYPES.has(type) || DISPLAY_ONLY_TYPES.has(type) || NOT_SAVED_TYPES.has(type)) return
      const orderId = question.orderId
      if (!TYPED_TYPES.has(type) && !hasOtherText(value)) {
        drop(orderId)
        send(orderId, value)
        return
      }
      drop(orderId)
      pending.set(orderId, { value, timer: setTimeout(() => flush(orderId), pauseMs) })
    },
    blur(orderId) {
      flush(orderId)
    },
    cancelPending() {
      cancelAll()
    },
  }
}
